import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  GoogleMap, 
  Marker, 
  InfoWindow,
  useLoadScript 
} from '@react-google-maps/api';

// Default coordinates for Delhi, India
const DEFAULT_LOCATION = {
  lat: 28.6139,
  lng: 77.2090,
  address: "Delhi, India (Default Location)"
};

// Map container style
const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid #ddd'
};

// Map options
const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: true,
  mapTypeControl: true,
  fullscreenControl: true,
};

/**
 * Reusable async function to get lat/lng from address
 * @param {string} address - Address string to geocode
 * @returns {Promise<Object>} - Object with lat, lng, and formattedAddress
 */
export async function getLatLngFromAddress(address) {
  // Validate input
  if (!address || typeof address !== 'string' || address.trim() === '') {
    throw new Error("Invalid address: Address is required and must be a non-empty string");
  }

  let processedAddress = address.trim();
  
  // Improve address parsing for comma-separated format
  if (processedAddress.includes(',')) {
    const parts = processedAddress.split(',').map(part => part.trim());
    
    // Handle "Country,State,City" format
    if (parts.length >= 3) {
      // Reorder to "City, State, Country" for better geocoding
      processedAddress = `${parts[2]}, ${parts[1]}, ${parts[0]}`;
    } else if (parts.length === 2) {
      // Handle "State,City" format
      processedAddress = `${parts[1]}, ${parts[0]}`;
    }
  }
  
  // Check if Google Maps is available
  if (typeof window === 'undefined' || !window.google || !window.google.maps) {
    throw new Error("Google Maps API not loaded");
  }

  const geocoder = new window.google.maps.Geocoder();
  
  // Add region restriction for India to improve accuracy
  const geocodeRequest = {
    address: processedAddress,
    componentRestrictions: {
      country: 'IN'  // Restrict to India
    }
  };
  
  return new Promise((resolve, reject) => {
    geocoder.geocode(geocodeRequest, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const location = results[0].geometry.location;
        const viewport = results[0].geometry.viewport;
        
        // Additional validation to ensure we're getting a reasonable result
        const lat = location.lat();
        const lng = location.lng();
        
        // Basic bounds check for India (approximate)
        const isInIndia = lat >= 8 && lat <= 37 && lng >= 68 && lng <= 97;
        
        if (!isInIndia) {
          reject(new Error("Geocoding result is outside India bounds"));
          return;
        }
        
        resolve({
          lat: lat,
          lng: lng,
          formattedAddress: results[0].formatted_address,
          placeId: results[0].place_id,
          viewport: viewport,
          locationType: results[0].geometry.location_type
        });
      } else {
        reject(new Error(`Geocoding failed: ${status}`));
      }
    });
  });
}

/**
 * Advanced Map Component with address support and InfoWindow
 * @param {Object} props - Component props
 * @param {number} props.lat - Latitude (optional)
 * @param {number} props.lng - Longitude (optional)
 * @param {string} props.address - Address string (optional)
 * @param {Object} props.mapContainerStyle - Custom container style (optional)
 * @param {number} props.zoom - Map zoom level (default: 15)
 */
const AdvancedMap = ({ 
  lat, 
  lng, 
  address, 
  mapContainerStyle = containerStyle,
  zoom = 15
}) => {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [infoWindowData, setInfoWindowData] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [mapInstance, setMapInstance] = useState(null);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyCFGhHTFklBNiQGjvV3h03rc_vN995emG0",
    libraries: ['places']
  });

  // Validate coordinates
  const isValidCoordinate = useCallback((value, min, max) => {
    const num = Number(value);
    return !isNaN(num) && isFinite(num) && num >= min && num <= max;
  }, []);

  // Determine appropriate zoom level based on location type
  const getZoomLevelForLocation = useCallback((addressString) => {
    if (!addressString || typeof addressString !== 'string') {
      return 15; // Default city level
    }
    
    const lowerAddress = addressString.toLowerCase();
    
    // Country level zoom (5-7)
    if (lowerAddress.includes('india') && !lowerAddress.includes('bihar') && !lowerAddress.includes('chapra')) {
      return 6;
    }
    
    // State level zoom (8-10)
    if (lowerAddress.includes('bihar') && !lowerAddress.includes('chapra')) {
      return 9;
    }
    
    // City level zoom (14-16)
    if (lowerAddress.includes('chapra')) {
      return 15;
    }
    
    // Default city level
    return 15;
  }, []);

  // Fallback locations for common places
  const getFallbackLocation = useCallback((addressString) => {
    if (!addressString || typeof addressString !== 'string') {
      return null;
    }
    
    const lowerAddress = addressString.toLowerCase();
    
    // Known locations in Bihar
    if (lowerAddress.includes('chapra') || lowerAddress.includes('chaprah')) {
      return {
        name: 'Chapra, Bihar, India',
        lat: 25.5459,
        lng: 84.8476,
        zoom: 15
      };
    }
    
    if (lowerAddress.includes('bihar')) {
      // State capital or central location
      return {
        name: 'Patna, Bihar, India',
        lat: 25.5941,
        lng: 85.1376,
        zoom: 12
      };
    }
    
    // Return null for unknown locations
    return null;
  }, []);

  // Validate props and determine initial position
  const getInitialPosition = useCallback(() => {
    // Case 1: Valid lat/lng provided directly
    if (isValidCoordinate(lat, -90, 90) && isValidCoordinate(lng, -180, 180)) {
      return {
        lat: Number(lat),
        lng: Number(lng),
        source: 'direct_coordinates',
        address: address || `Coordinates: ${lat}, ${lng}`
      };
    }
    
    // Case 2: Address provided (will be geocoded)
    if (address && typeof address === 'string' && address.trim()) {
      // Improve address parsing for better geocoding
      let processedAddress = address.trim();
      
      // Handle comma-separated format like "INDIA,Bihar,Chapra"
      if (processedAddress.includes(',')) {
        const parts = processedAddress.split(',').map(part => part.trim());
        if (parts.length >= 3) {
          // Convert "INDIA,Bihar,Chapra" to "Chapra, Bihar, India"
          processedAddress = `${parts[2]}, ${parts[1]}, ${parts[0]}`;
        } else if (parts.length === 2) {
          // Convert "Bihar,Chapra" to "Chapra, Bihar"
          processedAddress = `${parts[1]}, ${parts[0]}`;
        }
      }
      
      return {
        lat: DEFAULT_LOCATION.lat,
        lng: DEFAULT_LOCATION.lng,
        source: 'pending_geocoding',
        address: processedAddress
      };
    }
    
    // Case 3: Fallback to default
    return {
      ...DEFAULT_LOCATION,
      source: 'default'
    };
  }, [lat, lng, address, isValidCoordinate]);

  // Geocode address when provided
  const geocodeAddress = useCallback(async (addressToGeocode) => {
    if (!addressToGeocode) return null;
    
    try {
      setLoading(true);
      setError(null);
      const result = await getLatLngFromAddress(addressToGeocode);
      
      // Determine appropriate zoom level for the geocoded location
      const appropriateZoom = getZoomLevelForLocation(addressToGeocode);
      
      return {
        lat: result.lat,
        lng: result.lng,
        formattedAddress: result.formattedAddress,
        zoom: appropriateZoom,
        source: 'geocoded',
        locationType: result.locationType
      };
    } catch (err) {
      console.error('Geocoding error:', err);
      setError(`Failed to geocode address: ${err.message}`);
      
      // Try alternative approaches for common locations
      const fallbackResult = getFallbackLocation(addressToGeocode);
      if (fallbackResult) {
        setError(`Geocoding failed, using approximate location for ${fallbackResult.name}`);
        return {
          lat: fallbackResult.lat,
          lng: fallbackResult.lng,
          formattedAddress: fallbackResult.name,
          zoom: fallbackResult.zoom || 15,
          source: 'fallback'
        };
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  }, [getZoomLevelForLocation]);

  // Initialize map position
  useEffect(() => {
    const initialPos = getInitialPosition();
    
    if (initialPos.source === 'pending_geocoding') {
      // Geocode the address
      geocodeAddress(initialPos.address).then(geocodedResult => {
        if (geocodedResult) {
          setMarkerPosition({
            lat: geocodedResult.lat,
            lng: geocodedResult.lng
          });
          setInfoWindowData({
            position: { lat: geocodedResult.lat, lng: geocodedResult.lng },
            content: geocodedResult.formattedAddress
          });
          // Update zoom level after geocoding
          setMapZoom(geocodedResult.zoom || 15);
          // Update map instance if available
          if (mapInstance) {
            mapInstance.setCenter({ lat: geocodedResult.lat, lng: geocodedResult.lng });
            mapInstance.setZoom(geocodedResult.zoom || 15);
          }
        } else {
          // Fallback to default on geocoding failure
          setMarkerPosition({
            lat: DEFAULT_LOCATION.lat,
            lng: DEFAULT_LOCATION.lng
          });
          setInfoWindowData({
            position: { lat: DEFAULT_LOCATION.lat, lng: DEFAULT_LOCATION.lng },
            content: DEFAULT_LOCATION.address
          });
          setMapZoom(15); // Default city zoom for fallback
        }
      });
    } else {
      // Use direct coordinates or default
      setMarkerPosition({
        lat: initialPos.lat,
        lng: initialPos.lng
      });
      setInfoWindowData({
        position: { lat: initialPos.lat, lng: initialPos.lng },
        content: initialPos.address
      });
      
      // Set appropriate zoom for direct coordinates
      let appropriateZoom = zoom;
      if (initialPos.source === 'direct_coordinates') {
        appropriateZoom = 15; // City level for coordinates
      } else if (initialPos.source === 'default') {
        appropriateZoom = 15; // City level for default
      }
      setMapZoom(appropriateZoom);
      
      setLoading(false);
    }
  }, [getInitialPosition, geocodeAddress, mapInstance, zoom]);

  // Handle marker events
  const handleMarkerClick = useCallback(() => {
    setInfoWindowOpen(true);
  }, []);

  const handleMarkerMouseOver = useCallback(() => {
    setInfoWindowOpen(true);
  }, []);

  const handleMarkerMouseOut = useCallback(() => {
    setInfoWindowOpen(false);
  }, []);

  // Handle map load
  const handleMapLoad = useCallback((map) => {
    console.log('Map loaded successfully');
    setMapInstance(map);
    
    // Apply current zoom level to the loaded map
    if (map && markerPosition) {
      map.setCenter(markerPosition);
      map.setZoom(mapZoom);
    }
  }, [markerPosition, mapZoom]);

  // Handle map error
  const handleMapError = useCallback((error) => {
    console.error('Map error:', error);
    setError('Failed to load map');
    setLoading(false);
  }, []);

  // Memoized map center
  const mapCenter = useMemo(() => {
    return markerPosition || { lat: DEFAULT_LOCATION.lat, lng: DEFAULT_LOCATION.lng };
  }, [markerPosition]);

  // Loading and error states
  if (loadError) {
    return (
      <div style={{ ...mapContainerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <p>❌ Map failed to load</p>
          <p style={{ fontSize: '14px' }}>Please check your internet connection</p>
        </div>
      </div>
    );
  }

  if (!isLoaded || loading) {
    return (
      <div style={{ ...mapContainerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>🗺️</div>
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {error && (
        <div style={{ 
          position: 'absolute', 
          top: '10px', 
          left: '10px', 
          right: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '10px', 
          borderRadius: '4px', 
          zIndex: 1000,
          fontSize: '14px'
        }}>
          ⚠️ {error}
        </div>
      )}
      
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={mapZoom}
        options={mapOptions}
        onLoad={handleMapLoad}
        onError={handleMapError}
      >
        {markerPosition && (
          <>
            <Marker
              position={markerPosition}
              onClick={handleMarkerClick}
              onMouseOver={handleMarkerMouseOver}
              onMouseOut={handleMarkerMouseOut}
              animation={window.google?.maps?.Animation?.DROP}
            />
            
            {infoWindowData && infoWindowOpen && (
              <InfoWindow
                position={infoWindowData.position}
                onCloseClick={() => setInfoWindowOpen(false)}
              >
                <div style={{ padding: '10px', maxWidth: '200px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>📍 Location</h4>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.4' }}>
                    {infoWindowData.content}
                  </p>
                </div>
              </InfoWindow>
            )}
          </>
        )}
      </GoogleMap>
      
      {markerPosition && (
        <div style={{ 
          marginTop: '8px', 
          padding: '8px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '4px', 
          fontSize: '12px', 
          color: '#666' 
        }}>
          <strong>Position:</strong> {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
        </div>
      )}
    </div>
  );
};

export default AdvancedMap;